import { parseQuery } from 'services/QueryParser';
import { convertTimePropsToLocal } from 'services/utils';
import { fixTimestampJitter } from 'services/SensorHelper';
import { EVENT_TIME_PROPERTIES, STATUS_LIVE, STATUS_CLOSED, STATUS_INITIALIZING } from './ResourceStream';
import moment from 'moment';
import _uniq from 'lodash/uniq';
import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';

const DEFAULT_POLL_INTERVAL = 5000; // 5 seconds
const TOUCH_TO_IDENTIFY_POLL_INTERVAL = 3000; // 3 seconds

/**
 * PollBasedStream is a class that implements a polling mechanism to fetch
 * device event rather than an SSE-based stream (Server-Sent Events). This 
 * is intended to be used in scenarios where a firewall blocks long-lived 
 * TCP connections, making the SSE stream not viable.
 * 
 * This stream takes three different approaches to fetching data, depending on the params:
 * 1. Touch-to-identify: If the stream is for touch-to-identify (eventTypes is set to only "touch"),
 *    it will poll the device list every 3 seconds and will use event predicates to only fetch devices
 *    that have been touched within the last 3 seconds.
 * 2. [NOT YET IMPLEMENTED] Single device: If the stream is for a single device, it will poll the event 
 *    history of that device every 5 seconds to ensure all events are captured. This is intended for 
 *    plotting a graph.
 * 3. Device list: If the stream is for multiple devices, it will poll the device list every 5 seconds
 *    to get the latest data. This is intended for displaying a list of devices and their most recent events.
 * 
 * There are some quirks that comes with this implementation though:
 * - Event ID: When polling the device list (filtering on multiple devices or for touch-to-identify),
 *   we don't get the event ID from the API. It gets set to an empty string instead.
 * - Network status: When pulling the device list, the network status events are merged together
 *   into one event per heartbeat. This stream will split those up to mimic the behavior of an SSE stream,
 *   but it will send an event for every single CCON in the heartbeat every time (and not just new ones).
 */
export default class PollBasedStream {
    constructor(params, onUpdateCallback, sensorService) {
        if (params.filter) {
            // The filter is whatever the user has entered in the search box,
            // so it needs to be parsed and then removed from the params.
            const parsedFilter = parseQuery(params.filter);
            delete params.filter;
            
            // Add in anything that was parsed from the filter
            if (parsedFilter.deviceIds) {
                params.deviceIds = _uniq([...(params.deviceIds || []), ...parsedFilter.deviceIds]);
            }
            if (parsedFilter.deviceTypes) {
                params.deviceTypes = _uniq([...(params.deviceTypes || []), ...parsedFilter.deviceTypes]);
            }
            if (parsedFilter.labelFilters) {
                params.labelFilters = _uniq([...(params.labelFilters || []), ...parsedFilter.labelFilters]);
            }
            if (parsedFilter.query) {
                params.query = parsedFilter.query;
            }
        }

        this.params = params;
        this.onUpdateCallback = onUpdateCallback;
        this.sensorService = sensorService;
        this.status = STATUS_INITIALIZING;

        // Will be set to the interval ID of the polling timer.
        this.ticker = null;

        // Used to compare the previous data with the new data.
        this.previousData = null;

        this.start(params, onUpdateCallback);
    }

    start() {
        // Check if touch-to-identify stream
        if (_isEqual(this.params.eventTypes, ["touch"])) {
            const pollInterval = TOUCH_TO_IDENTIFY_POLL_INTERVAL;

            // Create a function to update the event predicate. This needs to be called
            // right before each poll to ensure we only get devices that have been touched
            // within the last poll interval.
            const updateEventPredicate = () => {
                // We want to search for devices that have been touched within the last poll
                // interval plus some margin to ensure we don't miss any events.
                const searchInterval = pollInterval + 1000;

                // This truncates "now" to second and removes the poll interval.
                // Format the timestamp to ISO8601.
                const ts = moment().milliseconds(0).subtract(searchInterval, 'milliseconds').toISOString();

                // This is a stream for touch-to-identify, so we need to use an
                // event predicate to only get devices that has recently been touched.
                this.params.eventPredicates = [`touch.updateTime >= "${ts}"`]
            };

            this.startPollingDeviceList(pollInterval, updateEventPredicate);
            return;
        }

        // TODO: Implement single device polling
        //
        // At the time of this writing, we don't start streams for single devices. Live-plotting
        // of graphs for single sensors uses the same stream as the device list. When using SSE-based
        // streams, this stream gets all events for the device, there was no need to start a new 
        // stream.
        //
        // For this poll-based stream, there is a possibility of dropping events if a device emits
        // multiple events within the poll interval. This can be somewhat mitigated by keeping the
        // poll interval fairly short.
        //
        // if (this.deviceIds && this.deviceIds.length === 1) {
        //     // If we have a single device, we need to poll its event history to
        //     // make sure we get all the events.
        //     this.startPollingEventHistory(DEFAULT_POLL_INTERVAL);
        //     return;
        // }

        // If the stream is not for touch-to-identify or a single device,
        // we need to poll the device list to get the latest data.
        this.startPollingDeviceList(DEFAULT_POLL_INTERVAL);
    }

    async startPollingDeviceList(pollInterval, prePollHook = () => {}) {
        // A flag to prevent multiple polls from running simultaneously.
        // This might happen if the API call takes longer than the poll interval.
        let pollInProgress = false;

        const poll = async () => {
            // Skip early if a poll is already in progress.
            if (pollInProgress) {
                return
            }
            pollInProgress = true;

            // Poll the device list to get the latest data.
            let response;
            try {
                prePollHook();
                response = await this.sensorService.sensors(this.params);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn("PollBasedStream: failed to fetch device list:", error);
                return;
            } finally {
                pollInProgress = false;
            }

            // For each device, check if they have received a new event since the last poll and publish them if so.
            response.data.forEach((newDevice) => {
                // Old device might be null. For example when the stream is for touch-to-identify,
                // the device being touched won't be in the previous data.
                const oldDevice = this.previousData?.find(d => d.id === newDevice.id);

                // Go through all event types in the new device and check if they have changed
                // compared to the previous data. If they have, publish the event.
                Object.keys(newDevice.reported || {}).forEach((eventType) => {
                    // Skip events that are not in the requested event types
                    if (this.params.eventTypes && this.params.eventTypes.length > 0 && !this.params.eventTypes.includes(eventType)) {
                        return;
                    }
    
                    const oldEvent = oldDevice?.reported[eventType];
                    const newEvent = newDevice.reported[eventType];

                    if (_isEqual(newEvent, oldEvent)) {
                        return; // No change in this event
                    }
    
                    if (eventType === 'networkStatus') {
                        // Network status events need special handling
                        this.publishNetworkStatusEvent(this.convertToStreamEvent(eventType, newDevice.name, newEvent));
                    } else {
                        this.publishEvent(this.convertToStreamEvent(eventType, newDevice.name, newEvent));
                    }
                });
            });

            this.previousData = response.data;
        }

        if (this.status === STATUS_INITIALIZING) {
            this.status = STATUS_LIVE;

            // First, get an initial list of devices so that we can compare
            // their events the next time we poll.
            try {
                prePollHook();
                const resp = await this.sensorService.sensors(this.params);
                this.previousData = resp.data;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn("PollBasedStream: failed to fetch initial device list. All events will be emitted on next poll.", error);
            }

            // Start the polling ticker
            this.ticker = setInterval(poll, pollInterval);
        }
    }

    async startPollingEventHistory(pollInterval) {
        const poll = () => {
            // eslint-disable-next-line no-console
            console.error("PollBasedStream for single device is not yet implemented");
        }

        if (this.status === STATUS_INITIALIZING) {
            this.status = STATUS_LIVE;

            // Start the polling ticker
            this.ticker = setInterval(poll, pollInterval);
        }
    }

    // Converts an event from the format received in the reported field of a 
    // device to the format expected by the stream.
    convertToStreamEvent(eventType, deviceName, event) {
        return {
            eventId: '', // We don't have the event ID when polling the device list
            targetName: deviceName,
            eventType: eventType,
            data: { [eventType]: event },
            timestamp: event.updateTime
        };
    }

    // Network status events behave differently when polled from the device
    // rather than streaming/listing the events themselves. All network status
    // events from the same heartbeat has merged together into one event. Since
    // the code that processes the streamed event is not prepared for that, we
    // need to generate a separate network status event for each CCON to mimic
    // the behavior of a stream.
    publishNetworkStatusEvent(newEvent) {
        newEvent.data.networkStatus.cloudConnectors.forEach(ccon => {
            const e = _cloneDeep(newEvent);
            e.data.networkStatus.signalStrength = ccon.signalStrength;
            e.data.networkStatus.rssi = ccon.rssi;
            e.data.networkStatus.cloudConnectors = [ccon];
            this.publishEvent(e);
        });
    }

    publishEvent(newEvent) {
        // Don't publish events if the stream is not live
        if (this.status !== STATUS_LIVE) {
            return; 
        }

        // Same event processing as in ResourceStream
        const event = convertTimePropsToLocal(newEvent, EVENT_TIME_PROPERTIES);
        event.timestamp = fixTimestampJitter(event.timestamp);

        // The event has changed, publish it to the callback
        this.onUpdateCallback(event);
    }

    stop() {
        this.status = STATUS_CLOSED;
        
        if (this.ticker) {
            clearInterval(this.ticker);
            this.ticker = null;
        }
    }
}
